import * as angleSlider from "@zag-js/angle-slider";
import { VanillaMachine, normalizeProps } from "@zag-js/vanilla";

import {
  Component,
  getString,
  getBoolean,
  getNumber,
  generateId,
  renderPart,
  getPartIds,
  valuesEqual,
} from "../lib";

export class AngleSlider extends Component<angleSlider.Props, angleSlider.Api> {
  initMachine(props: angleSlider.Props): VanillaMachine<any> {
    return new VanillaMachine(angleSlider.machine, props);
  }
  initApi(): angleSlider.Api {
    return angleSlider.connect(this.machine.service, normalizeProps);
  }
  render(): void {
    [
      "root",
      "label",
      "control",
      "thumb",
      "marker-group",
      "value-text",
      "hidden-input",
    ].forEach((part) => renderPart(this.el, part, this.api));
    ["marker"].forEach((part) =>
      renderPart(this.el, part, this.api, { value: "number" }),
    );
    this.updateValueText();
  }
  private updateValueText(): void {
    const valueEl = this.el.querySelector<HTMLElement>(
      '[data-part="value-text"] [data-part="value"]',
    );
    if (!valueEl) return;
    const numberingSystem = getString(this.el, "numberingSystem");
    if (numberingSystem) {
      valueEl.textContent = new Intl.NumberFormat(undefined, {
        numberingSystem,
      } as Intl.NumberFormatOptions).format(this.api.value);
    } else {
      valueEl.textContent = String(this.api.value);
    }
  }
}
export function initAngleSlider(
  doc: HTMLElement | Document = document,
  selector = ".angle-slider-js",
): void {
  doc.querySelectorAll<HTMLElement>(selector).forEach((rootEl) => {
    const angleSlider = new AngleSlider(rootEl, {
      id: generateId(rootEl, "angleSlider"),
      ids: getPartIds(rootEl, [
        "root",
        "label",
        "control",
        "thumb",
        "marker-group",
        "value-text",
        "hidden-input",
        "marker",
      ]),
      defaultValue: getNumber(rootEl, "defaultValue"),
      disabled: getBoolean(rootEl, "disabled"),
      invalid: getBoolean(rootEl, "invalid"),
      name: getString(rootEl, "name"),
      step: getNumber(rootEl, "step"),
      value: getNumber(rootEl, "value"),
      readOnly: getBoolean(rootEl, "readOnly"),
      onValueChangeEnd(details) {
        const eventName = getString(rootEl, "onValueChangeEnd");
        if (eventName) {
          rootEl.dispatchEvent(new CustomEvent(eventName, { detail: details }));
        }
      },
      onValueChange(details) {
        const eventName = getString(rootEl, "onValueChange");
        if (eventName) {
          rootEl.dispatchEvent(new CustomEvent(eventName, { detail: details }));
        }
      },
    });
    angleSlider.init();
    angleSlider.el.addEventListener("angle-slider:set-value", (event) => {
      const { value } = (event as CustomEvent<{ value: number }>).detail;
      if (!valuesEqual(angleSlider.api.value, value)) {
        angleSlider.api.setValue(value);
      }
    });
    angleSlider.el.addEventListener("angle-slider:value", (event) => {
      const callback = (
        event as CustomEvent<{ callback: (value: number) => void }>
      ).detail.callback;
      if (callback) callback(angleSlider.api.value);
    });
    angleSlider.el.addEventListener("angle-slider:value-degree", (event) => {
      const callback = (
        event as CustomEvent<{ callback: (value: string) => void }>
      ).detail.callback;
      if (callback) callback(angleSlider.api.valueAsDegree);
    });
  });
}
